// components/Admin/RichTextEditor.jsx
import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Bold, Italic, List, Heading2, Heading3, Link as LinkIcon, 
  Image as ImageIcon, Loader, Code, Undo, Redo, Quote, 
  ListOrdered, Minus, Table, Maximize2, Minimize2, 
  Search, X, Type, FileText
} from 'lucide-react'

const RichTextEditor = ({ value = '', onChange, placeholder = "Écrivez votre texte..." }) => {
  const [showPreview, setShowPreview] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [history, setHistory] = useState([value])
  const [historyIndex, setHistoryIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [replaceTerm, setReplaceTerm] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)
  const textareaRef = useRef(null)

  // Calculer les statistiques
  useEffect(() => {
    const words = value.trim().split(/\s+/).filter(w => w.length > 0).length
    const chars = value.length
    setWordCount(words)
    setCharCount(chars)
  }, [value])

  // Sauvegarder dans l'historique avec debounce
  const saveToHistory = (newValue) => {
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newValue)
    setHistory(newHistory)
    setHistoryIndex(newHistory.length - 1)
    onChange(newValue)
  }

  // Undo
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      onChange(history[newIndex])
    }
  }

  // Redo
  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      onChange(history[newIndex])
    }
  }

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        redo()
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        setShowSearch(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [historyIndex, history])

  const applyStyle = (before, after = '') => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)
    
    let newValue
    if (!selectedText) {
      newValue = value.substring(0, start) + before + after + value.substring(end)
    } else {
      newValue = 
        value.substring(0, start) + 
        before + selectedText + after + 
        value.substring(end)
    }
    
    saveToHistory(newValue)

    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = start + before.length
      textarea.selectionEnd = start + before.length + selectedText.length
    }, 0)
  }

  // Fonction pour appliquer les titres
  const applyHeading = (headingMarker) => {
    const textarea = textareaRef.current
    if (!textarea) {
      saveToHistory(value + '\n' + headingMarker + ' ')
      return
    }

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    let newValue
    let newCursorPos

    if (!selectedText) {
      newValue = value.substring(0, start) + headingMarker + ' ' + value.substring(end)
      newCursorPos = start + headingMarker.length + 1
    } else {
      let lineStart = start
      while (lineStart > 0 && value[lineStart - 1] !== '\n') {
        lineStart--
      }

      let lineEnd = end
      while (lineEnd < value.length && value[lineEnd] !== '\n') {
        lineEnd++
      }

      const lineContent = value.substring(lineStart, lineEnd)
      const existingHeading = lineContent.match(/^(#{1,4})\s/)
      
      if (existingHeading) {
        newValue = 
          value.substring(0, lineStart) + 
          headingMarker + ' ' + 
          lineContent.substring(existingHeading[0].length) + 
          value.substring(lineEnd)
      } else {
        newValue = 
          value.substring(0, lineStart) + 
          headingMarker + ' ' + 
          lineContent + 
          value.substring(lineEnd)
      }
      
      newCursorPos = lineStart + headingMarker.length + 1 + selectedText.length
    }
    
    saveToHistory(newValue)

    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = newCursorPos
      textarea.selectionEnd = newCursorPos
    }, 0)
  }

  const insertText = (text) => {
    const textarea = textareaRef.current
    if (!textarea) {
      saveToHistory(value + text + '\n')
      return
    }

    const start = textarea.selectionStart
    const newValue = value.substring(0, start) + text + '\n' + value.substring(start)
    saveToHistory(newValue)

    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = start + text.length + 1
      textarea.selectionEnd = start + text.length + 1
    }, 0)
  }

  // Insérer un tableau
  const insertTable = () => {
    const table = `
| Colonne 1 | Colonne 2 | Colonne 3 |
|-----------|-----------|-----------|
| Cellule 1 | Cellule 2 | Cellule 3 |
| Cellule 4 | Cellule 5 | Cellule 6 |
`
    insertText(table.trim())
  }

  // Rechercher et remplacer
  const handleSearch = () => {
    if (!searchTerm) return
    
    const textarea = textareaRef.current
    if (!textarea) return

    const index = value.indexOf(searchTerm, textarea.selectionStart)
    if (index !== -1) {
      textarea.focus()
      textarea.setSelectionRange(index, index + searchTerm.length)
    }
  }

  const handleReplace = () => {
    if (!searchTerm) return
    
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const selectedText = value.substring(start, start + searchTerm.length)
    
    if (selectedText === searchTerm) {
      const newValue = 
        value.substring(0, start) + 
        replaceTerm + 
        value.substring(start + searchTerm.length)
      saveToHistory(newValue)
      
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + replaceTerm.length, start + replaceTerm.length)
        handleSearch()
      }, 0)
    }
  }

  const handleReplaceAll = () => {
    if (!searchTerm) return
    
    const newValue = value.split(searchTerm).join(replaceTerm)
    saveToHistory(newValue)
  }

  const uploadImageToContent = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setUploading(true)

      if (file.size > 5 * 1024 * 1024) {
        alert('L\'image doit faire moins de 5MB')
        return
      }

      const mockUrl = URL.createObjectURL(file)
      insertText(`![${file.name}](${mockUrl})`)
    } catch (error) {
      console.error('Erreur upload:', error)
      alert('Erreur lors de l\'upload de l\'image')
    } finally {
      setUploading(false)
    }
  }

  const parseMarkdown = (text) => {
    if (!text) return ''

    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    html = html.replace(/!\[([^\]]*)\]\(https:\s+\/\//g, '![$1](https://')
    html = html.replace(/!\[([^\]]*)\]\(http:\s+\/\//g, '![$1](http://')

    const lines = html.split('\n')
    const result = []
    let i = 0

    while (i < lines.length) {
      const line = lines[i].trim()

      if (!line) {
        i++
        continue
      }

      // Titres
      if (/^# /.test(line)) {
        result.push(`<h1 class="text-3xl font-bold text-white mt-8 mb-4">${line.replace(/^# /, '')}</h1>`)
        i++
        continue
      }
      if (/^## /.test(line)) {
        result.push(`<h2 class="text-2xl font-bold text-white mt-6 mb-3">${line.replace(/^## /, '')}</h2>`)
        i++
        continue
      }
      if (/^### /.test(line)) {
        result.push(`<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-2">${line.replace(/^### /, '')}</h3>`)
        i++
        continue
      }
      if (/^#### /.test(line)) {
        result.push(`<h4 class="text-lg font-semibold text-gray-200 mt-3 mb-2">${line.replace(/^#### /, '')}</h4>`)
        i++
        continue
      }

      // Tableaux
      if (/^\|/.test(line)) {
        const tableLines = []
        while (i < lines.length && /^\|/.test(lines[i].trim())) {
          tableLines.push(lines[i].trim())
          i++
        }
        
        if (tableLines.length > 0) {
          const headerLine = tableLines[0]
          const headers = headerLine.split('|').filter(h => h.trim()).map(h => h.trim())
          
          let tableHtml = '<table class="min-w-full border border-slate-600 my-4"><thead class="bg-slate-700"><tr>'
          headers.forEach(header => {
            tableHtml += `<th class="border border-slate-600 px-4 py-2 text-left text-white">${header}</th>`
          })
          tableHtml += '</tr></thead><tbody>'
          
          for (let j = 2; j < tableLines.length; j++) {
            const cells = tableLines[j].split('|').filter(c => c.trim()).map(c => c.trim())
            tableHtml += '<tr class="hover:bg-slate-700/50">'
            cells.forEach(cell => {
              tableHtml += `<td class="border border-slate-600 px-4 py-2 text-gray-200">${cell}</td>`
            })
            tableHtml += '</tr>'
          }
          
          tableHtml += '</tbody></table>'
          result.push(tableHtml)
        }
        continue
      }

      // Listes à puces
      if (/^[\-\*\+] /.test(line)) {
        const listItems = []
        while (i < lines.length && /^[\-\*\+] /.test(lines[i].trim())) {
          listItems.push(`<li class="ml-6 text-gray-200">${lines[i].trim().replace(/^[\-\*\+] /, '')}</li>`)
          i++
        }
        result.push(`<ul class="list-disc space-y-1 my-3">${listItems.join('')}</ul>`)
        continue
      }

      // Listes numérotées
      if (/^\d+\. /.test(line)) {
        const listItems = []
        while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
          listItems.push(`<li class="ml-6 text-gray-200">${lines[i].trim().replace(/^\d+\. /, '')}</li>`)
          i++
        }
        result.push(`<ol class="list-decimal space-y-1 my-3">${listItems.join('')}</ol>`)
        continue
      }

      // Citations
      if (/^> /.test(line)) {
        const quotes = []
        while (i < lines.length && /^> /.test(lines[i].trim())) {
          quotes.push(lines[i].trim().replace(/^> /, ''))
          i++
        }
        result.push(`<blockquote class="border-l-4 border-blue-500 pl-4 italic text-gray-300 my-2">${quotes.join('<br />')}</blockquote>`)
        continue
      }

      // Bloc de code
      if (/^```/.test(line)) {
        const codeLines = []
        i++
        while (i < lines.length && !/^```/.test(lines[i])) {
          codeLines.push(lines[i])
          i++
        }
        result.push(`<pre class="bg-slate-900 border border-slate-600 rounded-lg p-4 my-3 overflow-x-auto"><code class="text-gray-200 font-mono text-sm">${codeLines.join('\n')}</code></pre>`)
        i++
        continue
      }

      // Ligne horizontale
      if (/^[\-\*_]{3,}$/.test(line)) {
        result.push('<hr class="border-t border-slate-600 my-4" />')
        i++
        continue
      }

      // Paragraphes
      const paragraphLines = []
      while (i < lines.length && lines[i].trim() && 
             !/^[#]{1,4} /.test(lines[i].trim()) &&
             !/^[\-\*\+] /.test(lines[i].trim()) &&
             !/^\d+\. /.test(lines[i].trim()) &&
             !/^> /.test(lines[i].trim()) &&
             !/^```/.test(lines[i].trim()) &&
             !/^\|/.test(lines[i].trim()) &&
             !/^[\-\*_]{3,}$/.test(lines[i].trim())) {
        paragraphLines.push(lines[i])
        i++
      }

      if (paragraphLines.length > 0) {
        let content = paragraphLines.join('\n')

        const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g
        let lastIndex = 0
        const parts = []
        let match

        while ((match = imageRegex.exec(content)) !== null) {
          if (match.index > lastIndex) {
            let textBefore = content.substring(lastIndex, match.index).trim()
            if (textBefore) {
              parts.push({ type: 'text', content: textBefore })
            }
          }
          parts.push({ type: 'image', alt: match[1], src: match[2].trim() })
          lastIndex = match.index + match[0].length
        }

        if (lastIndex < content.length) {
          let textAfter = content.substring(lastIndex).trim()
          if (textAfter) {
            parts.push({ type: 'text', content: textAfter })
          }
        }

        if (parts.length === 0) {
          parts.push({ type: 'text', content: content })
        }

        parts.forEach(part => {
          if (part.type === 'image') {
            result.push(`<img src="${part.src}" alt="${part.alt}" class="w-full max-w-2xl h-auto rounded-lg my-4 border border-slate-600" />`)
          } else {
            let formattedContent = part.content

            // IMPORTANT: Traiter d'abord les patterns les plus longs (*** avant ** avant *)
            // Gras + Italique (***texte***)
            formattedContent = formattedContent.replace(/\*\*\*([^\*]+)\*\*\*/g, '<strong><em class="font-bold italic text-white">$1</em></strong>')
            // Code inline
            formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code class="bg-slate-900 text-orange-400 px-2 py-1 rounded font-mono text-sm">$1</code>')
            // Gras (__texte__ ou **texte**)
            formattedContent = formattedContent.replace(/__([^_]+)__/g, '<strong class="font-bold text-white">$1</strong>')
            formattedContent = formattedContent.replace(/\*\*([^\*]+)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
            // Italique (_texte_ ou *texte*)
            formattedContent = formattedContent.replace(/_([^_]+)_/g, '<em class="italic text-gray-300">$1</em>')
            formattedContent = formattedContent.replace(/\*([^\*]+)\*/g, '<em class="italic text-gray-300">$1</em>')
            // Liens
            formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-300 underline">$1</a>')
            // Retours à la ligne
            formattedContent = formattedContent.replace(/\n/g, '<br />')

            result.push(`<p class="text-gray-200 leading-relaxed">${formattedContent}</p>`)
          }
        })
      }
    }

    return result.join('')
  }

  return (
    <div className={`w-full space-y-3 ${isFullscreen ? 'fixed inset-0 z-50 bg-slate-900 p-6 overflow-auto' : ''}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 bg-slate-700 p-3 rounded-lg">
        {/* Undo/Redo */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={undo}
          disabled={historyIndex === 0}
          title="Annuler (Ctrl+Z)"
          className={`p-2 rounded transition-colors ${
            historyIndex === 0 
              ? 'bg-slate-600/50 text-gray-500 cursor-not-allowed' 
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          <Undo className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={redo}
          disabled={historyIndex === history.length - 1}
          title="Refaire (Ctrl+Y)"
          className={`p-2 rounded transition-colors ${
            historyIndex === history.length - 1
              ? 'bg-slate-600/50 text-gray-500 cursor-not-allowed' 
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          <Redo className="w-4 h-4" />
        </motion.button>

        <div className="w-px bg-slate-500" />

        {/* Formatage de texte */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyStyle('**', '**')}
          title="Gras"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Bold className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyStyle('_', '_')}
          title="Italique"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Italic className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyStyle('`', '`')}
          title="Code inline"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Code className="w-4 h-4" />
        </motion.button>

        <div className="w-px bg-slate-500" />

        {/* Titres */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyHeading('#')}
          title="Titre 1"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors text-xs font-bold"
        >
          H1
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyHeading('##')}
          title="Titre 2"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Heading2 className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => applyHeading('###')}
          title="Titre 3"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Heading3 className="w-4 h-4" />
        </motion.button>

        <div className="w-px bg-slate-500" />

        {/* Listes et structure */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => insertText('- ')}
          title="Liste à puces"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <List className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => insertText('1. ')}
          title="Liste numérotée"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <ListOrdered className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => insertText('> ')}
          title="Citation"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Quote className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => insertText('---')}
          title="Ligne horizontale"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Minus className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={insertTable}
          title="Insérer un tableau"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <Table className="w-4 h-4" />
        </motion.button>

        <div className="w-px bg-slate-500" />

        {/* Média */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => insertText('[Lien](https://...)')}
          title="Lien"
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          <LinkIcon className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => document.getElementById('image-editor-input')?.click()}
          disabled={uploading}
          title="Ajouter une image"
          className="p-2 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-600/50 rounded text-white transition-colors"
        >
          {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </motion.button>

        <input
          id="image-editor-input"
          type="file"
          accept="image/*"
          onChange={uploadImageToContent}
          className="hidden"
        />

        <div className="flex-1" />

        {/* Outils */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowSearch(!showSearch)}
          title="Rechercher (Ctrl+F)"
          className={`p-2 rounded transition-colors ${
            showSearch
              ? 'bg-blue-600 text-white'
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          <Search className="w-4 h-4" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsFullscreen(!isFullscreen)}
          title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
          className="p-2 bg-slate-600 hover:bg-slate-500 rounded text-white transition-colors"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </motion.button>

        <button
          onClick={() => setShowPreview(!showPreview)}
          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
            showPreview 
              ? 'bg-green-600 text-white' 
              : 'bg-slate-600 hover:bg-slate-500 text-white'
          }`}
        >
          {showPreview ? 'Éditer' : 'Aperçu'}
        </button>
      </div>

      {/* Barre de recherche/remplacement */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-slate-700 p-4 rounded-lg space-y-3"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium">Rechercher et remplacer</h3>
              <button
                onClick={() => setShowSearch(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={replaceTerm}
                  onChange={(e) => setReplaceTerm(e.target.value)}
                  placeholder="Remplacer par..."
                  className="w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              >
                Suivant
              </button>
              <button
                onClick={handleReplace}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
              >
                Remplacer
              </button>
              <button
                onClick={handleReplaceAll}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
              >
                Remplacer tout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compteur de mots/caractères */}
      <div className="flex items-center gap-4 text-xs text-gray-400 bg-slate-700 px-3 py-2 rounded">
        <div className="flex items-center gap-2">
          <Type className="w-3 h-3" />
          <span>{wordCount} mots</span>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="w-3 h-3" />
          <span>{charCount} caractères</span>
        </div>
      </div>

      {/* Editor ou Preview */}
      {!showPreview ? (
        <textarea
          ref={textareaRef}
          name="editor"
          value={value}
          onChange={(e) => saveToHistory(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-slate-800 border border-slate-600 rounded-lg p-4 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 resize-none font-mono text-sm ${
            isFullscreen ? 'h-[calc(100vh-300px)]' : 'h-96'
          }`}
        />
      ) : (
        <div 
          className={`w-full bg-slate-800 border border-slate-600 rounded-lg p-4 overflow-y-auto ${
            isFullscreen ? 'h-[calc(100vh-300px)]' : 'max-h-96'
          }`}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(value) }}
        />
      )}

      <div className="text-xs text-gray-400 space-y-1">
        <div>**gras** | ***gras+italique*** | _italique_ | `code` | # H1 | ## H2 | ### H3</div>
        <div>- Liste | 1. Numérotée | &gt; Citation | [Lien](url) | ![Image](url) | --- Ligne</div>
        <div>| Col1 | Col2 | pour créer un tableau</div>
      </div>
    </div>
  )
}

export default RichTextEditor